import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  GetInvoiceService,
  UpdateInvoiceStageAndArchiveStatusService,
} from "../../../../Services/InvoiceService/InvoiceService";
import AlertMessage from "../../../AlertMessage/AlertMessage";
import ConfirmPopup from "../../../ConfirmPopup/ConfirmPopup";
import {
  createSupplierStatementWorkbook,
  getSupplierStatementExportFilename,
} from "./supplierStatementExport";

const EXPORT_ERROR =
  "Something went wrong. The export could not be completed. Please try again.";

interface ExportButtonProps {
  invoiceId: number | null;
}

const ExportButton: React.FC<ExportButtonProps> = ({ invoiceId }) => {
  const [open, setOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const exportInProgress = useRef(false);

  const handleExport = async () => {
    if (exportInProgress.current) return;
    exportInProgress.current = true;
    setIsExporting(true);
    try {
      if (invoiceId === null || !Number.isFinite(invoiceId) || invoiceId <= 0) {
        throw new Error("Cannot export a supplier statement without a valid item ID.");
      }

      const statement = await GetInvoiceService(invoiceId);
      if (!statement || statement.invoiceId !== invoiceId) {
        throw new Error("The latest supplier statement could not be loaded.");
      }

      const workbook = createSupplierStatementWorkbook({
        invoiceId: statement.invoiceId,
        supplier: statement.supplier,
        invoiceDate: statement.invoiceDate,
        statementPeriod: statement.statementPeriod,
        outstandingBalance: statement.outstandingBalance ?? statement.totalAmount,
        totalAmount: statement.totalAmount,
        description: statement.description,
        reconciliationItems: statement.reconciliationItems,
      });

      XLSX.writeFile(
        workbook,
        getSupplierStatementExportFilename({
          invoiceId: statement.invoiceId,
          supplier: statement.supplier,
          invoiceDate: statement.invoiceDate,
          statementPeriod: statement.statementPeriod,
          outstandingBalance: statement.outstandingBalance ?? statement.totalAmount,
          totalAmount: statement.totalAmount,
          description: statement.description,
        }),
        { bookType: "xlsx", cellStyles: true },
      );

      const updateResult = await UpdateInvoiceStageAndArchiveStatusService({
        invoiceIds: [invoiceId],
        stageId: 22,
        archiveStatus: null,
      });
      if (!updateResult.success) {
        AlertMessage({
          type: "error",
          text: "The statement was downloaded, but its status could not be updated.",
        });
        return;
      }

      setOpen(false);
    } catch (error) {
      console.error("Failed to export supplier statement:", error);
      AlertMessage({ type: "error", text: EXPORT_ERROR });
    } finally {
      exportInProgress.current = false;
      setIsExporting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="sv-action-btn sv-btn-export"
        onClick={() => setOpen(true)}
      >
        <span className="sv-btn-text">Export</span>
      </button>

      <ConfirmPopup
        blur
        isOpen={open}
        title="Export Statement"
        message={
          <div style={{ textAlign: "center" }}>
      
            <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "6px" }}>1 supplier statement</div>
            <div style={{ color: "#555", fontSize: "13px", lineHeight: "1.6" }}>
              Are you sure you want to export this statement?
            </div>
          </div>
        }
        confirmLabel={isExporting ? "Exporting..." : "Export"}
        cancelLabel="Cancel"
        confirmColor="var(--color-brand)"
        onConfirm={() => void handleExport()}
        onCancel={() => setOpen(false)}
        confirmDisabled={isExporting}
      />
    </>
  );
};

export default ExportButton;
